import React, {FC, useState} from "react";
import {GridItem, HorizontalFlexLayout} from "components/common/FlexLayout";
import {Controller, FormProvider, useForm} from "react-hook-form";
import InputField from "components/common/InputField";
import strings from "res/strings";
import BudgetField from "components/common/BudgetField";
import Button from "components/common/Button";
import Offer from "models/Offer";
import {handleFieldErrors, toDateInput} from "util/Utilities";
import Api from "data/remote/Api";
import ErrorResponse from "data/remote/models/ErrorResponse";
import LookupFields from "components/common/LookupFields";
import jobTypes from "res/jobTypes";
import LocationField from "components/common/LocationField";

interface Props {
    offer: Offer
    onComplete: (offer: Offer) => void
}

const UpsertOfferControl: FC<Props> = (props) => {
    const [loading, setLoading] = useState(false)
    const form = useForm<Offer>({
        defaultValues: {
            ...props.offer,
            location: props.offer.job?.location,
            deadline: toDateInput(props.offer.job?.deadline),
            type: props.offer.job?.type
        }
    })

    const onSubmitOffer = form.handleSubmit(async fields => {
        if (loading) {
            return
        }

        form.clearErrors()
        fields.deadline = new Date(fields.deadline).toISOString()

        if (fields.budget?.value) {
            fields.budget.value = Number.parseFloat(fields.budget.value.toString())
        } else {
            fields.budget.currency = ""
        }

        setLoading(true)

        if (fields.id) {
            await Api.offers
                .updateOffer(fields)
                .then(o => {
                    //@ts-ignore
                    fields.etag = o?.etag
                    props.onComplete(fields)
                })
                .catch(res => handleFieldErrors(res.data as ErrorResponse, form.setError))
        } else {
            await Api.offers
                .createOffer(fields)
                .then(o => {
                    fields.id = o!.id
                    fields.status = "draft"
                    props.onComplete(fields)
                })
                .catch(res => handleFieldErrors(res.data as ErrorResponse, form.setError))
        }

        setLoading(false)
    })

    return (
        <FormProvider {...form}>
            <form onSubmit={onSubmitOffer}>
                <HorizontalFlexLayout spacing={2}>
                    <GridItem xs={12}>
                        <Controller
                            name="name"
                            control={form.control}
                            render={({field, fieldState: {error}}) => (
                                <InputField
                                    label={"Name"}
                                    placeholder={strings.jobNamePlaceholder}
                                    required
                                    fieldError={error}
                                    {...field} />
                            )}/>
                    </GridItem>
                    <GridItem xs={12} md={6}>
                        <LocationField withLabel/>
                    </GridItem>
                    <GridItem xs={12} md={6}>
                        <Controller
                            name="type"
                            control={form.control}
                            render={({field, fieldState: {error}}) => (
                                <LookupFields
                                    label={strings.projectType}
                                    items={jobTypes}
                                    required
                                    fieldError={error}
                                    {...field}/>
                            )}/>
                    </GridItem>
                    <GridItem xs={12} md={6}>
                        <Controller
                            name="deadline"
                            control={form.control}
                            render={({field, fieldState: {error}}) => (
                                <InputField
                                    label={strings.deadline}
                                    type={"date"}
                                    required
                                    fieldError={error}
                                    {...field} />
                            )}/>
                    </GridItem>
                    <GridItem xs={12} md={6}>
                        <BudgetField
                            namespace="budget"
                            label={strings.budget}/>
                    </GridItem>
                    <GridItem xs={12}>
                        <Controller
                            name="notes"
                            control={form.control}
                            render={({field, fieldState: {error}}) => (
                                <InputField
                                    label={strings.notes}
                                    multiline
                                    fieldError={error}
                                    {...field} />
                            )}/>
                    </GridItem>
                    <GridItem xs={12}>
                        <HorizontalFlexLayout
                            justify={"flex-end"}>
                            <Button
                                isBusy={loading}
                                type="submit"
                                variant={"contained"}>
                                {props.offer.id ? strings.save : strings.next}
                            </Button>
                        </HorizontalFlexLayout>
                    </GridItem>
                </HorizontalFlexLayout>
            </form>
        </FormProvider>
    )
}

export default UpsertOfferControl
